import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Search, 
  UserCheck, 
  UserX, 
  Eye, 
  DollarSign,
  ArrowLeft,
  Calendar,
  Mail,
  Building,
  Phone,
  BarChart3,
  Trash2,
  RefreshCw
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { UserService } from '../../services/userService';
import { TransactionService } from '../../services/transactionService';
import LoadingSpinner from '../common/LoadingSpinner';
import { useNotification } from '../common/Notification';
import { AlertContext } from '../../contexts/AlertContext';
import { useContext } from 'react';

const UserManagement = () => {
  const { currentUser } = useAuth();
  const { addNotification } = useNotification();
  const { addAlert } = useContext(AlertContext);
  
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showRechargeModal, setShowRechargeModal] = useState(false);
  const [showUserTransactionsModal, setShowUserTransactionsModal] = useState(false);
  const [userTransactions, setUserTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [rechargeAmount, setRechargeAmount] = useState('');
  const [rechargeReason, setRechargeReason] = useState('');
  const [rechargeMethod, setRechargeMethod] = useState('credit');
  const [rechargeDirection, setRechargeDirection] = useState('credit');
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [users, searchTerm, statusFilter]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const allUsers = await UserService.getAllUsers();
      setUsers(allUsers);
    } catch (error) {
      console.error('Erreur lors du chargement des utilisateurs:', error);
      addAlert('error', 'Erreur de chargement', 'Erreur lors du chargement des utilisateurs', 'high');
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = () => {
    let filtered = users;

    // Filtrer par terme de recherche
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(user => 
        user.firstName?.toLowerCase().includes(term) ||
        user.lastName?.toLowerCase().includes(term) ||
        user.email?.toLowerCase().includes(term) ||
        user.company?.toLowerCase().includes(term)
      );
    }

    // Filtrer par statut
    if (statusFilter !== 'all') {
      filtered = filtered.filter(user => user.status === statusFilter);
    }

    setFilteredUsers(filtered);
  };

  const handleActivateUser = async (userId) => {
    setActionLoading(prev => ({ ...prev, [`activate-${userId}`]: true }));
    
    try {
      await UserService.activateReseller(userId, currentUser.uid);
      addNotification('success', 'Utilisateur activé', 'Utilisateur activé avec succès');
      await loadUsers();
    } catch (error) {
      console.error('Erreur lors de l\'activation:', error);
      addNotification('error', 'Erreur d\'activation', error.message || 'Erreur lors de l\'activation');
    } finally {
      setActionLoading(prev => ({ ...prev, [`activate-${userId}`]: false }));
    }
  };

  const handleDeactivateUser = async (userId) => {
    const reason = prompt('Raison de la désactivation (optionnel):');
    if (reason === null) return;
    
    setActionLoading(prev => ({ ...prev, [`deactivate-${userId}`]: true }));
    
    try {
      await UserService.deactivateReseller(userId, currentUser.uid, reason);
      addNotification('success', 'Utilisateur désactivé', 'Utilisateur désactivé avec succès');
      await loadUsers();
    } catch (error) {
      console.error('Erreur lors de la désactivation:', error);
      addNotification('error', 'Erreur de désactivation', error.message || 'Erreur lors de la désactivation');
    } finally {
      setActionLoading(prev => ({ ...prev, [`deactivate-${userId}`]: false }));
    }
  };

  const handleRecharge = async () => {
    if (!selectedUser || !rechargeAmount || parseFloat(rechargeAmount) <= 0) {
      addNotification('error', 'Montant invalide', 'Veuillez saisir un montant valide');
      return;
    }

    setActionLoading(prev => ({ ...prev, 'recharge': true }));
    
    try {
      await UserService.rechargeUserBalance(
        selectedUser.id,
        parseFloat(rechargeAmount),
        rechargeReason,
        currentUser.uid,
        {
          movementType: rechargeMethod,
          direction: rechargeDirection
        }
      );
      
      const movementLabel = TransactionService.getBalanceMethodLabel(rechargeMethod);
      const targetLabel = rechargeDirection === 'debit'
        ? 'crédit'
        : rechargeMethod === 'credit'
          ? 'crédit'
          : 'solde';
      const actionLabel = rechargeDirection === 'debit'
        ? `déduit du ${targetLabel}`
        : `ajouté au ${targetLabel}`;

      addNotification('success', 'Solde mis à jour', `${rechargeAmount} DZD ${actionLabel} en ${movementLabel.toLowerCase()}`);
      setShowRechargeModal(false);
      setRechargeAmount('');
      setRechargeReason('');
      setRechargeMethod('credit');
      setRechargeDirection('credit');
      setSelectedUser(null);
      await loadUsers();
    } catch (error) {
      console.error('Erreur lors de la recharge:', error);
      addNotification('error', 'Erreur de recharge', error.message || 'Erreur lors de la recharge');
    } finally {
      setActionLoading(prev => ({ ...prev, 'recharge': false }));
    }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir archiver l'utilisateur ${userEmail} ?\n\nCette action va désactiver son accès et conserver l'historique pour l'audit.`)) {
      return;
    }

    setActionLoading(prev => ({ ...prev, [`delete-${userId}`]: true }));
    
    try {
      await UserService.deleteUser(userId);
      addNotification('success', 'Utilisateur archivé', 'Accès utilisateur désactivé avec conservation de l\'historique');
      await loadUsers();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      addNotification('error', 'Erreur de suppression', error.message || 'Erreur lors de la suppression');
    } finally {
      setActionLoading(prev => ({ ...prev, [`delete-${userId}`]: false }));
    }
  };

  const resolveUserCreditBalance = async (user) => {
    const storedCredit = Number(user?.creditBalance);
    const safeStoredCredit = Number.isFinite(storedCredit) ? Math.max(0, storedCredit) : 0;

    if (!user?.id) {
      return safeStoredCredit;
    }

    try {
      const derivedCredit = await TransactionService.getDerivedUserCreditBalance(user.id, { includeLegacy: true });
      return TransactionService.resolveEffectiveCreditBalance(user, derivedCredit, { allowBalanceFallback: true });
    } catch (error) {
      console.error('Erreur lors du calcul du crédit utilisateur:', error);
      return TransactionService.resolveEffectiveCreditBalance(user, safeStoredCredit, { allowBalanceFallback: true });
    }
  };

  const loadFreshSelectedUser = async (user) => {
    if (!user?.id) {
      return null;
    }

    try {
      const freshUser = await UserService.getUserProfile(user.id);
      const resolvedCreditBalance = await resolveUserCreditBalance(freshUser);
      const hydratedUser = {
        ...freshUser,
        resolvedCreditBalance
      };

      setSelectedUser(hydratedUser);
      return hydratedUser;
    } catch (error) {
      console.error('Erreur lors du chargement du profil utilisateur:', error);
      const resolvedCreditBalance = await resolveUserCreditBalance(user);
      const fallbackUser = {
        ...user,
        resolvedCreditBalance
      };

      setSelectedUser(fallbackUser);
      return fallbackUser;
    }
  };

  const openUserDetailsModal = async (user) => {
    await loadFreshSelectedUser(user);
    setShowUserModal(true);
  };

  const openRechargeUserModal = async (user) => {
    await loadFreshSelectedUser(user);
    setShowRechargeModal(true);
  };

  const handleSyncUsers = async () => {
    setSyncLoading(true);
    
    try {
      const result = await UserService.syncUsers();
      addNotification('success', 'Synchronisation terminée', result.message);
      setShowSyncModal(false);
      await loadUsers();
    } catch (error) {
      console.error('Erreur lors de la synchronisation:', error);
      addNotification('error', 'Erreur de synchronisation', error.message || 'Erreur lors de la synchronisation');
    } finally {
      setSyncLoading(false);
    }
  };

  const loadUserTransactions = async (userId) => {
    setTransactionsLoading(true);
    try {
      const transactions = await TransactionService.getAllUserTransactions(userId, { includeLegacy: true });
      setUserTransactions(transactions);
      setShowUserTransactionsModal(true);
    } catch (error) {
      console.error('Erreur lors du chargement des transactions:', error);
      addNotification('error', 'Erreur de chargement', 'Erreur lors du chargement des transactions');
    } finally {
      setTransactionsLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-DZ', {
      style: 'currency',
      currency: 'DZD',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp.seconds * 1000).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDisplayedCreditBalance = (user) => {
    if (!user) {
      return 0;
    }

    if (typeof user.resolvedCreditBalance === 'number') {
      return user.resolvedCreditBalance;
    }

    return Number(user.creditBalance || 0);
  };

  const getBalanceTransactionLabel = (transaction) => {
    if (transaction.type === 'recharge') {
      return TransactionService.getBalanceMovementLabel(transaction);
    }

    return 'Achat';
  };

  const getBalanceTransactionBadgeClass = (transaction) => {
    if (transaction.type !== 'recharge') {
      return 'bg-blue-100 text-blue-800';
    }

    if (transaction.direction === 'debit') {
      return transaction.method === 'cash'
        ? 'bg-cyan-100 text-cyan-800'
        : transaction.method === 'payment'
          ? 'bg-amber-100 text-amber-800'
          : 'bg-slate-100 text-slate-800';
    }

    return transaction.method === 'cash'
      ? 'bg-sky-100 text-sky-800'
      : transaction.method === 'payment'
        ? 'bg-emerald-100 text-emerald-800'
        : 'bg-green-100 text-green-800';
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-success-100 text-success-800',
      pending: 'bg-warning-100 text-warning-800',
      inactive: 'bg-danger-100 text-danger-800'
    };

    const labels = {
      active: 'Actif',
      pending: 'En attente',
      inactive: 'Inactif'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        {labels[status] || status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="xl" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <Link
                to="/admin"
                className="mr-4 p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <h1 className="text-xl font-semibold text-gray-900">
                Gestion des utilisateurs
              </h1>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Rechercher par nom, email ou entreprise..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div className="sm:w-48">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="all">Tous les statuts</option>
                <option value="active">Actifs</option>
                <option value="pending">En attente</option>
                <option value="inactive">Inactifs</option>
              </select>
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between">
            <p className="text-sm text-gray-600">
              {filteredUsers.length} utilisateur(s) trouvé(s)
            </p>
            
            <button
              onClick={() => setShowSyncModal(true)}
              disabled={syncLoading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
            >
              {syncLoading ? (
                <LoadingSpinner size="sm" className="mr-2" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Synchroniser les utilisateurs
            </button>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Utilisateur
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Entreprise
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Solde
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Inscription
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                          <span className="text-primary-600 font-medium text-sm">
                            {user.firstName?.[0]}{user.lastName?.[0]}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {user.firstName} {user.lastName}
                          </div>
                          <div className="text-sm text-gray-500">
                            {user.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{user.company || 'N/A'}</div>
                      <div className="text-sm text-gray-500">{user.phone || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {formatCurrency(user.balance)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(user.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(user.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        {/* View Transactions */}
                        <button
                          onClick={() => {
                            setSelectedUser(user);
                            loadUserTransactions(user.id);
                          }}
                          disabled={transactionsLoading}
                          className="p-2 text-purple-400 hover:text-purple-600 transition-colors disabled:opacity-50"
                          title="Voir les transactions"
                        >
                          {transactionsLoading ? (
                            <LoadingSpinner size="sm" />
                          ) : (
                            <BarChart3 className="w-4 h-4" />
                          )}
                        </button>

                        {/* View Details */}
                        <button
                          onClick={() => openUserDetailsModal(user)}
                          className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                          title="Voir les détails"
                        >
                          <Eye className="w-4 h-4" />
                        </button>

                        {/* Recharge */}
                        <button
                          onClick={() => openRechargeUserModal(user)}
                          className="p-2 text-blue-400 hover:text-blue-600 transition-colors"
                          title="Recharger le compte"
                        >
                          <DollarSign className="w-4 h-4" />
                        </button>

                        {/* Activate/Deactivate */}
                        {user.status === 'pending' && (
                          <button
                            onClick={() => handleActivateUser(user.id)}
                            disabled={actionLoading[`activate-${user.id}`]}
                            className="p-2 text-success-400 hover:text-success-600 transition-colors disabled:opacity-50"
                            title="Activer"
                          >
                            {actionLoading[`activate-${user.id}`] ? (
                              <LoadingSpinner size="sm" />
                            ) : (
                              <UserCheck className="w-4 h-4" />
                            )}
                          </button>
                        )}

                        {user.status === 'active' && (
                          <button
                            onClick={() => handleDeactivateUser(user.id)}
                            disabled={actionLoading[`deactivate-${user.id}`]}
                            className="p-2 text-danger-400 hover:text-danger-600 transition-colors disabled:opacity-50"
                            title="Désactiver"
                          >
                            <UserX className="w-4 h-4" />
                          </button>
                        )}

                        {/* Delete User */}
                        <button
                          onClick={() => handleDeleteUser(user.id, user.email)}
                          disabled={actionLoading[`delete-${user.id}`]}
                          className="p-2 text-red-400 hover:text-red-600 transition-colors disabled:opacity-50"
                          title="Supprimer définitivement"
                        >
                          {actionLoading[`delete-${user.id}`] ? (
                            <LoadingSpinner size="sm" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredUsers.length === 0 && (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                <Search className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-gray-500">Aucun utilisateur trouvé</p>
              <p className="text-sm text-gray-400 mt-1">
                Essayez de modifier vos critères de recherche
              </p>
            </div>
          )}
        </div>
      </div>

      {/* User Details Modal */}
      {showUserModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  Détails de l'utilisateur
                </h3>
                <button
                  onClick={() => setShowUserModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  ×
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Informations personnelles</h4>
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <Mail className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-600">{selectedUser.email}</span>
                    </div>
                    <div className="flex items-center">
                      <Phone className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-600">{selectedUser.phone || 'N/A'}</span>
                    </div>
                    <div className="flex items-center">
                      <Building className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-600">{selectedUser.company || 'N/A'}</span>
                    </div>
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-600">
                        Inscrit le {formatDate(selectedUser.createdAt)}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Informations du compte</h4>
                  <div className="space-y-3">
                    <div>
                      <span className="text-sm text-gray-500">Statut:</span>
                      <div className="mt-1">{getStatusBadge(selectedUser.status)}</div>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Solde actuel:</span>
                      <div className="mt-1 text-lg font-semibold text-gray-900">
                        {formatCurrency(selectedUser.balance)}
                      </div>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Crédit en cours:</span>
                      <div className="mt-1 text-lg font-semibold text-blue-700">
                        {formatCurrency(getDisplayedCreditBalance(selectedUser))}
                      </div>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Rôle:</span>
                      <div className="mt-1 text-sm text-gray-900 capitalize">
                        {selectedUser.role || 'reseller'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recharge Modal */}
      {showRechargeModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {rechargeDirection === 'debit' ? 'Récupérer le crédit' : 'Ajouter au solde'}
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                {selectedUser.firstName} {selectedUser.lastName}
              </p>
            </div>
            
            <div className="p-6">
              <div className="space-y-4">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3">
                    <p className="text-xs font-medium uppercase tracking-wide text-emerald-700">
                      Solde actuel
                    </p>
                    <p className="mt-1 text-xl font-semibold text-emerald-800">
                      {formatCurrency(selectedUser.balance || 0)}
                    </p>
                  </div>
                  <div className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3">
                    <p className="text-xs font-medium uppercase tracking-wide text-blue-700">
                      Crédit en cours
                    </p>
                    <p className="mt-1 text-xl font-semibold text-blue-800">
                      {formatCurrency(getDisplayedCreditBalance(selectedUser))}
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Montant (DZD)
                  </label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={rechargeAmount}
                    onChange={(e) => setRechargeAmount(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder={rechargeDirection === 'debit' ? 'Montant à récupérer du crédit' : 'Montant à ajouter'}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type de transfert
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { key: 'credit', label: 'Crédit' },
                      { key: 'payment', label: 'Paiement' },
                      { key: 'cash', label: 'Espèces' }
                    ].map((option) => (
                      <button
                        key={option.key}
                        type="button"
                        onClick={() => setRechargeMethod(option.key)}
                        className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                          rechargeMethod === option.key
                            ? 'border-primary-500 bg-primary-50 text-primary-700'
                            : 'border-gray-300 text-gray-600 hover:border-gray-400'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Action
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { key: 'credit', label: 'Ajouter' },
                      { key: 'debit', label: 'Déduire' }
                    ].map((option) => (
                      <button
                        key={option.key}
                        type="button"
                        onClick={() => setRechargeDirection(option.key)}
                        className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                          rechargeDirection === option.key
                            ? option.key === 'debit'
                              ? 'border-red-500 bg-red-50 text-red-700'
                              : 'border-green-500 bg-green-50 text-green-700'
                            : 'border-gray-300 text-gray-600 hover:border-gray-400'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Raison (optionnel)
                  </label>
                  <textarea
                    value={rechargeReason}
                    onChange={(e) => setRechargeReason(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder={rechargeDirection === 'debit' ? 'Note sur la récupération...' : 'Note sur le transfert...'}
                  />
                </div>
              </div>
            </div>
            
            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowRechargeModal(false);
                  setRechargeAmount('');
                  setRechargeReason('');
                  setRechargeMethod('credit');
                  setRechargeDirection('credit');
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleRecharge}
                disabled={!rechargeAmount || parseFloat(rechargeAmount) <= 0 || actionLoading.recharge}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
              >
                {actionLoading.recharge ? (
                  <>
                    <LoadingSpinner size="sm" className="mr-2" />
                    Recharge...
                  </>
                ) : (
                  rechargeDirection === 'debit' ? 'Déduire' : 'Ajouter'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Transactions Modal */}
      {showUserTransactionsModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  Transactions de {selectedUser.firstName} {selectedUser.lastName}
                </h3>
                <button
                  onClick={() => {
                    setShowUserTransactionsModal(false);
                    setUserTransactions([]);
                    setSelectedUser(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>
            </div>
            
            <div className="p-6">
              {userTransactions.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500">Aucune transaction trouvée pour cet utilisateur</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Description
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Montant
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Statut
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {userTransactions.map((transaction) => (
                        <tr key={transaction.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {new Date(transaction.createdAt?.seconds ? transaction.createdAt.seconds * 1000 : transaction.createdAt).toLocaleDateString('fr-FR')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              getBalanceTransactionBadgeClass(transaction)
                            }`}>
                              {getBalanceTransactionLabel(transaction)}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {transaction.description || transaction.productName || 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {(transaction.type === 'recharge' && transaction.direction === 'debit' ? '-' : '') + formatCurrency(transaction.amount)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              transaction.status === 'validated' ? 'bg-green-100 text-green-800' :
                              transaction.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              transaction.status === 'rejected' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {transaction.status === 'validated' ? 'Validé' :
                               transaction.status === 'pending' ? 'En attente' :
                               transaction.status === 'rejected' ? 'Rejeté' :
                               transaction.status || 'Complété'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button
                              onClick={() => {
                                setSelectedTransaction(transaction);
                                setShowTransactionModal(true);
                              }}
                              className="text-primary-600 hover:text-primary-900"
                            >
                              Voir détails
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Transaction Detail Modal */}
      {showTransactionModal && selectedTransaction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  Détails de la transaction
                </h3>
                <button
                  onClick={() => {
                    setShowTransactionModal(false);
                    setSelectedTransaction(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Type</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {getBalanceTransactionLabel(selectedTransaction)}
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {selectedTransaction.description || selectedTransaction.productName || 'N/A'}
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Montant</label>
                  <p className="mt-1 text-sm text-gray-900 font-medium">
                    {(selectedTransaction.type === 'recharge' && selectedTransaction.direction === 'debit' ? '-' : '') + formatCurrency(selectedTransaction.amount)}
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Date</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {new Date(selectedTransaction.createdAt?.seconds ? selectedTransaction.createdAt.seconds * 1000 : selectedTransaction.createdAt).toLocaleString('fr-FR')}
                  </p>
                </div>
                
                {selectedTransaction.reason && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Raison</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {selectedTransaction.reason}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmation de synchronisation */}
      {showSyncModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center mr-3">
                  <RefreshCw className="w-5 h-5 text-primary-600" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    Synchroniser les utilisateurs
                  </h3>
                  <p className="text-sm text-gray-500">
                    Nettoyer les utilisateurs orphelins
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowSyncModal(false)}
                className="text-gray-400 hover:text-gray-600"
                disabled={syncLoading}
              >
                ×
              </button>
            </div>
            
            <div className="p-6">
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  Cette action va :
                </p>
                <ul className="text-sm text-gray-600 space-y-1 ml-4">
                  <li>• Vérifier tous les utilisateurs dans Firestore</li>
                  <li>• Supprimer les utilisateurs qui n'existent plus dans Firebase Auth</li>
                  <li>• Nettoyer les données orphelines</li>
                </ul>
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-yellow-800">
                      Cette action est irréversible. Les utilisateurs supprimés ne pourront pas être récupérés.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowSyncModal(false)}
                  disabled={syncLoading}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSyncUsers}
                  disabled={syncLoading}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                >
                  {syncLoading ? (
                    <>
                      <LoadingSpinner size="sm" className="mr-2" />
                      Synchronisation...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Synchroniser
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
